import { Injectable } from '@angular/core';
import {
  AGENT_MODEL,
  CommonRegEx,
  DevicesService,
  HelpersService,
  TestLayerValue,
  TestListEntity,
  TestMode,
  TestTypeValue,
  UnmanagedDevicesService,
  LmapService
} from '@nrwl/common-lib';
import { TruespeedConfigFieldMapping } from '@nrwl/truespeed-test';
import {
  IPAddressValue,
  Y1564ConfigFieldMapping,
  definedAddressValue,
  vlanConfigValue
} from '@nrwl/y1564-test';
import { isNil } from 'lodash';
import { ServiceConfigFieldMapping } from '@nrwl/service-test';
import { MultistepConfigMapping } from '@nrwl/multi-step-test';
import { RFC2544ConfigFieldMapping } from '@nrwl/rfc2544-test';
import { VoipConfigMapping } from '@nrwl/voip-test';
import { Y1731ConfigFieldMapping } from '@nrwl/y1731-test';
import { TwampReflectorConfigMapping } from '@nrwl/twamp-reflector-test';
import { UDPConfigFieldMapping } from '@nrwl/udp-test';
import { HttpConfigFieldMapping } from '@nrwl/http-test';
import { FtpConfigFieldMapping } from '@nrwl/ftp-test';
import { TwampTestSetupConfigService } from './twamp-test-setup-config.service';
import { TwampConfigFieldMapping } from '@nrwl/twamp-test';
import { BertConfigFieldMapping } from '@nrwl/bert-test';
import { TEST_DURATIONS } from 'libs/udp-test/src/lib/udp-test/setup/models/models';
interface SchedulerFormat {
  timeStamp: string;
  repeatInterval: number;
  endTime: any;
  isValid: boolean;
}

// This service will acts as intermediate which load the params and override the default test model values,
// It can be used for edit/clone and loading template params.
@Injectable()
export class TestSetupConfigService {
  selectedTestEntity: TestListEntity;
  testConfig: any;
  mode: string;
  constructor(
    private readonly deviceService: DevicesService,
    private readonly unmanagedDevicesService: UnmanagedDevicesService,
    private readonly twampTestConfig: TwampTestSetupConfigService,
    private readonly helperService: HelpersService,
    private readonly lmap: LmapService
  ) {}
  //eslint-disable-next-line
  populateTestOutputParams(testConfig: any, testDataEntity: TestListEntity, mode: string): any {
    this.mode = mode;
    this.selectedTestEntity = testDataEntity;
    this.testConfig = testConfig;
    let testType = this.selectedTestEntity.getTestType();
    if (testType === TestTypeValue.Y1731) {
      testType = 'y1731';
    }
    const testParams = this.getTestParams(testType, testConfig);
    return this.getConfigParamsByType(testParams);
  }

  // Get the required params which need to set in the default model for each test type
  //eslint-disable-next-line
  getConfigParamsByType(testParams: any) {
    const testType = this.selectedTestEntity.getTestType();
    if (testType === TestTypeValue.TRUESPEED) {
      return this.getTruespeedOutputParams(testParams);
    }
    if (testType === TestTypeValue.Y1564) {
      return this.getY1564OutputParams(testParams);
    }
    if (testType === TestTypeValue.BERT) {
      return this.getBertOutputParams(testParams);
    }
    if (testType === TestTypeValue.RFC2544) {
      return this.getRfc2544OutputParams(testParams);
    }
    if (testType === TestTypeValue.SERVICE_CONTINUITY) {
      return this.getServiceOutputParams();
    }
    if (testType === TestTypeValue.MULTISTEP) {
      return this.getMultistepOutputParams();
    }
    if (testType === TestTypeValue.VOIP) {
      return this.getVoipOutputParams();
    }
    if (testType === TestTypeValue.TWAMP) {
      return this.getTwampOutputParams();
    }
    if (testType === TestTypeValue.Y1731) {
      return this.getY1731OutputParams(testParams);
    }
    if (
      testType === TestTypeValue.TWAMP_LIGHT_REFLECTOR ||
      testType === TestTypeValue.TWAMP_REFLECTOR
    ) {
      return this.getTwampReflectorOutputParams(testType);
    }
    if (testType === TestTypeValue.UDP) {
      return this.getUdpOutputParams(testParams);
    }
    if (testType === TestTypeValue.HTTP) {
      return this.getHttpOutputParams(testParams);
    }
    if (testType === TestTypeValue.FTP) {
      return this.getFtpOutputParams(testParams);
    }
  }

  //eslint-disable-next-line
  getBertOutputParams(testParams: any): any {
    let config = {};
    const masterData = testParams['bert:bert'] || testParams['viavi-solutions-bert:bert'];
    config[BertConfigFieldMapping.TEST_MODE] =
      this.testConfig['config']['test']?.['extended-config']?.test?.mode;
    config[BertConfigFieldMapping.ENGINE_A_INFO] = masterData['engine-a-info'];
    config[BertConfigFieldMapping.ENGINE_B_INFO] = masterData['engine-b-info'];
    config[BertConfigFieldMapping.SRC_IPV4_ADDRESS] = masterData['engine-a-info']?.['test-ip'];
    config[BertConfigFieldMapping.SRC_SUBNET] = masterData['engine-a-info']?.['test-subnet-mask'];
    config[BertConfigFieldMapping.SRC_GATEWAY] = masterData['engine-a-info']?.['test-gateway'];
    config[BertConfigFieldMapping.DEST_IPV4_ADDRESS] = masterData['engine-b-info']?.['test-ip'];
    config[BertConfigFieldMapping.SCHEDULER] = this.getScheduledParams(this.testConfig);
    config[BertConfigFieldMapping.DEVICE_SELECTION_METHOD] =
      masterData['group'][0][BertConfigFieldMapping.DEVICE_SELECTION_METHOD];
    config[BertConfigFieldMapping.SWAP_MAC_ADDRESS] =
      masterData[BertConfigFieldMapping.SWAP_MAC_ADDRESS];
    config[BertConfigFieldMapping.STREAM_NAME] =
      masterData['group'][0][BertConfigFieldMapping.STREAM_NAME];
    config[BertConfigFieldMapping.STREAMS] = this.getBertFlatternedStreamData(
      masterData['group'][0][BertConfigFieldMapping.STREAMS]
    );
    const streamsConfig = {
      ...config[BertConfigFieldMapping.STREAMS][0]['l2-config'],
      ...config[BertConfigFieldMapping.STREAMS][0]['l3-ipv4-config']
    };
    config[BertConfigFieldMapping.BERT_PATTERN] =
      config[BertConfigFieldMapping.STREAMS][0][BertConfigFieldMapping.BERT_PATTERN];
    config[BertConfigFieldMapping.VERIFY_PRIORITY] =
      config[BertConfigFieldMapping.STREAMS]?.[0]?.['l2-config']?.[
        BertConfigFieldMapping.VERIFY_PRIORITY
      ];
    const commonParams = this.getCommonAgentTestparams();
    config[BertConfigFieldMapping.DEFINED_ADDRESS] = this.getBertExtendedPluginConfig(
      masterData['bert-parameters']
    );
    config[BertConfigFieldMapping.VLAN_CONFIG] = streamsConfig[BertConfigFieldMapping.CVLAN_ID]
      ? vlanConfigValue.Q_IN_Q
      : streamsConfig[BertConfigFieldMapping.SVLAN_ID]
      ? vlanConfigValue.SINGLE_VLAN
      : vlanConfigValue.UNTAGGED;
    config = {
      ...config,
      ...commonParams,
      ...streamsConfig,
      ...masterData['bert-parameters']
    };
    const groupedStreams = this.getBertStreamsFromGroupedConfig();
    if (groupedStreams) {
      config[BertConfigFieldMapping.STREAMS] = groupedStreams;
    }
    return config;
  }

  getBertFlatternedStreamData(streamData: Array<any>): Array<any> {
    const flatternedStreamData = [];
    streamData.forEach(stream => {
      const data = {
        ...stream,
        ...stream['l2-config'],
        ...stream['l3-ipv4-config']
      };
      flatternedStreamData.push(data);
    });
    return flatternedStreamData;
  }

  getBertStreamsFromGroupedConfig(): any {
    const lmapObj = this.testConfig?.config?.config?.[0]?.primaryConfig;
    const schedule = this.lmap.extractScheduleByTaskName(lmapObj, 'bert');
    const bertOption = schedule?.action?.[0]?.option?.find((opt: any) => opt.id === 'bert');
    const bertValue = bertOption ? JSON.parse(bertOption.value) : null;
    const configStreams =
      (bertValue?.['viavi-solutions-bert:bert'] || bertValue?.['bert:bert'])?.group?.[0]?.streams ||
      [];
    let streamData = [];
    let streamObj = {};
    if (configStreams?.length > 0) {
      configStreams.forEach((stream, index) => {
        streamObj[BertConfigFieldMapping.STREAM_NUMBER] =
          Number(stream['stream-number']) || index + 1;
        streamObj[BertConfigFieldMapping.STREAM_NAME] = stream['name'];
        if (stream[BertConfigFieldMapping.OVER_HEAD_BYTES] !== undefined) {
          streamObj[BertConfigFieldMapping.OVER_HEAD_BYTES] =
            stream[BertConfigFieldMapping.OVER_HEAD_BYTES];
        }
        if (stream['fail-on-OOS'] !== undefined) {
          streamObj[BertConfigFieldMapping.ERROR_ON_OOS] =
            stream['fail-on-OOS'] === 'true' ? true : false;
        } else {
          streamObj[BertConfigFieldMapping.ERROR_ON_OOS] = undefined;
        }
        streamObj[BertConfigFieldMapping.ULR] =
          Number(stream[BertConfigFieldMapping.ULR]) || Number(stream['ulr-from-engine-a']);
        streamObj[BertConfigFieldMapping.LOSS] = Number(stream['frame-loss-ratio-threshold']);
        if (stream['frame-delay-threshold']) {
          streamObj[BertConfigFieldMapping.FD] = Number(stream['frame-delay-threshold']);
        } else {
          streamObj[BertConfigFieldMapping.FD] = null;
        }
        streamObj[BertConfigFieldMapping.FDV] = Number(stream['latency-FDV-maximum']);
        streamObj[BertConfigFieldMapping.BER_THRESHOLD] =
          stream[BertConfigFieldMapping.BER_THRESHOLD] || stream['ber-threshold'];
        streamObj[BertConfigFieldMapping.TOS] = Number(stream['l3-ipv4-config']['tos']);
        streamObj[BertConfigFieldMapping.TTL] = Number(stream['l3-ipv4-config']['ttl']);
        streamObj[BertConfigFieldMapping.BERT_FRAME_SIZE] =
          stream[BertConfigFieldMapping.BERT_FRAME_SIZE] ||
          stream['l2-config']['bert-frame-size-list'];
        streamObj[BertConfigFieldMapping.CVLAN_PRIORITY] = Number(
          stream['l2-config']['clan-priority']
        );
        streamObj[BertConfigFieldMapping.SVLAN_PRIORITY] = Number(
          stream['l2-config']['vlan-priority']
        );
        streamObj = this.removeNullValues(streamObj);
        streamData = [...streamData, { ...streamObj }];
      });
    }

    return streamData;
  }

  //eslint-disable-next-line
  getBertExtendedPluginConfig(params: any): any {
    const pluginConfig = this.testConfig['config']['test']?.['extended-config']?.['pluginConfig'];
    //default address type for edit mode
    let addressType = definedAddressValue.MANUAL;
    if (pluginConfig) {
      if (params[Y1564ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L2) {
        addressType =
          pluginConfig.connections?.connections?.[0]?.['source']?.['layer2SrcType'] || addressType;
      } else {
        if (params[Y1564ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4) {
          addressType =
            pluginConfig.connections?.connections?.[0]?.['source']?.['ipv4SrcType'] || addressType;
        }
      }
    }
    return addressType;
  }

  //eslint-disable-next-line
  getUdpOutputParams(testParams: any): any {
    const masterData = testParams['udp:udp'];
    const config = {};
    config[UDPConfigFieldMapping.ENGINE_A_INFO] = masterData['engine-a-info'];
    config[UDPConfigFieldMapping.ENGINE_B_INFO] = masterData['engine-b-info'];
    config[UDPConfigFieldMapping.TEST_MODE] = this.testConfig['config']['agent']?.['secondary']
      ? TestMode.MA_TO_MA
      : TestMode.CLIENT_TO_MA;
    if (this.testConfig?.config?.test?.['extended-config']) {
      const streams =
        this.testConfig?.config?.test?.['extended-config']?.pluginConfig?.connections
          ?.connections[0]?.streams;
      if (streams?.length > 0) {
        const direction = streams[0]?.strmUdpDirection;
        if (direction === 'engine-a-to-engine-b' || direction === 'unidirectional-a2b') {
          config[UDPConfigFieldMapping.DIRECTION] = 'unidirectional-a2b';
          config[UDPConfigFieldMapping.STREAMS_A2B] = this.getStreamsData(this.testConfig, 'A2B');
        } else if (direction === 'engine-b-to-engine-a' || direction === 'unidirectional-b2a') {
          config[UDPConfigFieldMapping.DIRECTION] = 'unidirectional-b2a';
          config[UDPConfigFieldMapping.STREAMS_B2A] = this.getStreamsData(this.testConfig, 'B2A');
        } else if (direction === 'bidirectional' || direction === 'concurrent-bidirectional') {
          config[UDPConfigFieldMapping.DIRECTION] = 'concurrent-bidirectional';
          config[UDPConfigFieldMapping.STREAMS_A2B] = this.getStreamsData(this.testConfig, 'A2B');
          config[UDPConfigFieldMapping.STREAMS_B2A] = this.getStreamsData(this.testConfig, 'B2A');
        } else {
          config[UDPConfigFieldMapping.DIRECTION] = 'sequential-bidirectional';
          config[UDPConfigFieldMapping.STREAMS_A2B] = this.getStreamsData(this.testConfig, 'A2B');
          config[UDPConfigFieldMapping.STREAMS_B2A] = this.getStreamsData(this.testConfig, 'B2A');
        }
      }
    } else {
      const udpConfig = this.getTestParams('udp', this.testConfig);
      const streams = udpConfig['udp:udp']?.streams;
      if (streams?.length > 0) {
        const direction = streams[0]?.mode;
        if (direction === 'engine-a-to-engine-b' || direction === 'unidirectional-a2b') {
          config[UDPConfigFieldMapping.DIRECTION] = 'unidirectional-a2b';
          config[UDPConfigFieldMapping.STREAMS_A2B] = this.getConfigStreamsData(
            this.testConfig,
            'A2B',
            streams
          );
        } else if (direction === 'engine-b-to-engine-a' || direction === 'unidirectional-b2a') {
          config[UDPConfigFieldMapping.DIRECTION] = 'unidirectional-b2a';
          config[UDPConfigFieldMapping.STREAMS_B2A] = this.getConfigStreamsData(
            this.testConfig,
            'B2A',
            streams
          );
        } else if (direction === 'bidirectional' || direction === 'concurrent-bidirectional') {
          config[UDPConfigFieldMapping.DIRECTION] = 'concurrent-bidirectional';
          config[UDPConfigFieldMapping.STREAMS_A2B] = this.getConfigStreamsData(
            this.testConfig,
            'A2B',
            streams
          );
          config[UDPConfigFieldMapping.STREAMS_B2A] = this.getConfigStreamsData(
            this.testConfig,
            'B2A',
            streams
          );
        } else {
          config[UDPConfigFieldMapping.DIRECTION] = 'sequential-bidirectional';
          config[UDPConfigFieldMapping.STREAMS_A2B] = this.getConfigStreamsData(
            this.testConfig,
            'A2B',
            streams
          );
          config[UDPConfigFieldMapping.STREAMS_B2A] = this.getConfigStreamsData(
            this.testConfig,
            'B2A',
            streams
          );
        }
      }
    }

    config[UDPConfigFieldMapping.EXPIRATION_DATE] =
      this.testConfig.config.test?.controller?.['expiration-date'] || null;
    config[UDPConfigFieldMapping.MAX_EXECUTION_COUNTS] =
      this.testConfig.config.test?.controller?.['max-exec-count'] || null;
    config[UDPConfigFieldMapping.SHA_ID] = this.testConfig.sha;
    const extendedSetup = this.testConfig?.config?.test?.['extended-config']?.pluginConfig?.setup;
    const durationList = TEST_DURATIONS;
    const customDuration = durationList.some(
      item => item.value === masterData['udp-parameters']?.duration
    );
    if (extendedSetup?.testDuration === 'custom') {
      config[UDPConfigFieldMapping.TEST_DURATION] = 'custom';
      config[UDPConfigFieldMapping.TEST_CUSTOM_DURATION] = extendedSetup?.testCustomDuration;
    } else {
      if (
        this.testConfig['config']['test']?.['extended-config']?.TestDuration === 'custom' ||
        masterData['udp-parameters']?.isCustomDuration ||
        !customDuration
      ) {
        config[UDPConfigFieldMapping.TEST_DURATION] = 'custom';
        config[UDPConfigFieldMapping.TEST_CUSTOM_DURATION] = this.helperService.convertSecondsToHMS(
          masterData['udp-parameters']?.duration
        );
      } else {
        config[UDPConfigFieldMapping.TEST_DURATION] = masterData['udp-parameters']?.duration;
      }
    }
    if (extendedSetup) {
      if (extendedSetup?.bandwidthConvention === 'ir') {
        config[UDPConfigFieldMapping.STREAM_RATE_CONVENTION] = 'l2-mbps';
      } else {
        config[UDPConfigFieldMapping.STREAM_RATE_CONVENTION] = 'l1-mbps';
      }
      if (extendedSetup?.vlanPresent === 'No' || extendedSetup?.vlanPresent === 46) {
        config[UDPConfigFieldMapping.VLAN] = 46;
      } else if (extendedSetup?.vlanPresent === 'Yes' || extendedSetup?.vlanPresent === 50) {
        config[UDPConfigFieldMapping.VLAN] = 50;
      } else {
        config[UDPConfigFieldMapping.VLAN] = 54;
      }
    } else {
      config[UDPConfigFieldMapping.STREAM_RATE_CONVENTION] =
        masterData['udp-parameters']?.['unit-convention'] || 'l2-mbps';
      config[UDPConfigFieldMapping.VLAN] = masterData['udp-parameters']?.vlan || 46;
    }
    config[UDPConfigFieldMapping.SCHEDULER] = this.getScheduledParams(this.testConfig);
    const commonParams = this.getCommonAgentTestparams();
    return { ...commonParams, ...config };
  }

  //eslint-disable-next-line
  getStreamsData(testConfig, tableChange: string): any {
    const allStreams =
      testConfig?.config?.test?.['extended-config']?.pluginConfig?.connections?.connections[0]
        ?.streams;
    const a2bStreamList = [];
    const b2aStreamList = [];
    let filteredStreams = [];
    const a2bStreamNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    allStreams.forEach(item => {
      if (a2bStreamNumbers.includes(item.strmNumber)) {
        a2bStreamList.push(item);
      } else {
        b2aStreamList.push(item);
      }
    });
    if (tableChange === 'A2B') {
      filteredStreams = a2bStreamList;
    } else {
      filteredStreams = b2aStreamList;
    }
    let newStreams = [];
    const streamObj = {};
    if (filteredStreams?.length > 0) {
      filteredStreams.forEach(stream => {
        streamObj[UDPConfigFieldMapping.STREAM_NUMBER] = stream.strmNumber;
        streamObj[UDPConfigFieldMapping.STREAM_NAME] = stream.strmName;
        streamObj[UDPConfigFieldMapping.STREAM_TYPE] = stream.strmUdpType;
        streamObj[UDPConfigFieldMapping.VIDEO_TYPE] = stream.strmVideoType;
        streamObj[UDPConfigFieldMapping.COMPRESSION] = stream.strmVideoCompression;
        streamObj[UDPConfigFieldMapping.CHANNELS] = stream.strmNumberChannels;
        streamObj[UDPConfigFieldMapping.CODEC] = stream.strmVoiceCodec;
        streamObj[UDPConfigFieldMapping.SAMPLING_RATE] = stream.strmVoiceSampleRate;
        streamObj[UDPConfigFieldMapping.CALLS] = stream.strmNumberCalls;
        streamObj[UDPConfigFieldMapping.STREAM_L2_IR_A2B] = stream.strmSlaULROrIR;
        streamObj[UDPConfigFieldMapping.UDP_BW_A2B] = stream.strmSlaCir;
        streamObj[UDPConfigFieldMapping.STREAM_L2_IR_B2A] = stream.strmSlaULROrIR2;
        streamObj[UDPConfigFieldMapping.UDP_BW_B2A] = stream.strmSlaCir2;
        streamObj[UDPConfigFieldMapping.JITTER_THRESHOLD] = stream.strmFrameJitter;
        streamObj[UDPConfigFieldMapping.FRAME_SIZE] = stream.strmFrameSize;
        streamObj[UDPConfigFieldMapping.POCKET_LOSS_RATIO] = stream.strmPacketLossRatio;
        streamObj[UDPConfigFieldMapping.UDP_PORT] = stream.strmUdpPort;
        if (stream.strmInUse === undefined) {
          streamObj[UDPConfigFieldMapping.STREAM_IN_USE] = true;
        } else {
          streamObj[UDPConfigFieldMapping.STREAM_IN_USE] = stream.strmInUse;
        }
        newStreams = [...newStreams, { ...streamObj }];
      });
    }
    return newStreams;
  }

  //eslint-disable-next-line
  getConfigStreamsData(testConfig, tableChange: string, allStreams: any): any {
    const a2bStreamList = [];
    const b2aStreamList = [];
    let filteredStreams = [];
    const a2bStreamNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    allStreams.forEach(item => {
      if (a2bStreamNumbers.includes(item['stream-number'])) {
        a2bStreamList.push(item);
      } else {
        b2aStreamList.push(item);
      }
    });
    if (tableChange === 'A2B') {
      filteredStreams = a2bStreamList;
    } else {
      filteredStreams = b2aStreamList;
    }
    let newStreams = [];
    const streamObj = {};
    if (filteredStreams?.length > 0) {
      filteredStreams.forEach(stream => {
        streamObj[UDPConfigFieldMapping.STREAM_NUMBER] =
          stream[UDPConfigFieldMapping.STREAM_NUMBER];
        streamObj[UDPConfigFieldMapping.STREAM_NAME] = stream[UDPConfigFieldMapping.STREAM_NAME];
        streamObj[UDPConfigFieldMapping.STREAM_TYPE] = stream[UDPConfigFieldMapping.STREAM_TYPE];
        if (stream[UDPConfigFieldMapping.STREAM_TYPE] === undefined) {
          streamObj[UDPConfigFieldMapping.STREAM_TYPE] = 'Data';
        } else {
          if (stream[UDPConfigFieldMapping.STREAM_TYPE] === 'Video') {
            streamObj[UDPConfigFieldMapping.VIDEO_TYPE] = stream['video-type'];
            streamObj[UDPConfigFieldMapping.COMPRESSION] =
              stream[UDPConfigFieldMapping.COMPRESSION];
            streamObj[UDPConfigFieldMapping.CHANNELS] = stream[UDPConfigFieldMapping.CHANNELS];
          } else if (stream[UDPConfigFieldMapping.STREAM_TYPE] === 'Voice') {
            streamObj[UDPConfigFieldMapping.CODEC] = stream[UDPConfigFieldMapping.CODEC];
            streamObj[UDPConfigFieldMapping.SAMPLING_RATE] =
              stream[UDPConfigFieldMapping.SAMPLING_RATE];
            streamObj[UDPConfigFieldMapping.CALLS] = stream[UDPConfigFieldMapping.CALLS];
          }
        }
        streamObj[UDPConfigFieldMapping.STREAM_L2_IR_A2B] = stream['ir'] || '10.00';
        streamObj[UDPConfigFieldMapping.UDP_BW_A2B] = stream['bw'];
        streamObj[UDPConfigFieldMapping.STREAM_L2_IR_B2A] = stream['ir'] || '10.00';
        streamObj[UDPConfigFieldMapping.UDP_BW_B2A] = stream['bw'];
        streamObj[UDPConfigFieldMapping.JITTER_THRESHOLD] =
          stream[UDPConfigFieldMapping.JITTER_THRESHOLD];
        streamObj[UDPConfigFieldMapping.FRAME_SIZE] =
          stream['actual-frame-size'] || stream[UDPConfigFieldMapping.FRAME_SIZE] || '512';
        streamObj[UDPConfigFieldMapping.POCKET_LOSS_RATIO] = stream['packet-loss-ratio-threshold'];
        streamObj[UDPConfigFieldMapping.UDP_PORT] = stream[UDPConfigFieldMapping.UDP_PORT];
        if (stream.strmInUse === undefined) {
          streamObj[UDPConfigFieldMapping.STREAM_IN_USE] = true;
        }
        newStreams = [...newStreams, { ...streamObj }];
      });
    }
    return newStreams;
  }

  getTwampReflectorOutputParams(testType: string): any {
    let config = {};
    const originatorId = this.selectedTestEntity.getOriginatorAgent()?.deviceData?.id;
    const orginatorDevice = this.deviceService.getDeviceById(originatorId);
    const commonParams = this.getCommonAgentTestparams();
    commonParams[TwampReflectorConfigMapping.SELECTED_ORIGINATOR] = orginatorDevice;
    commonParams[TwampReflectorConfigMapping.AGENT_TYPE] = orginatorDevice.getDeviceType();
    if (orginatorDevice.getDeviceType()?.includes('JMEP')) {
      config[TwampReflectorConfigMapping.UDP_PORT] =
        orginatorDevice.getDevice().capabilities?.['simpleTestConfig']['twamp-udp-port'] || 5000;
    } else {
      config[TwampReflectorConfigMapping.REFLECTOR_MODE] =
        testType === TestTypeValue.TWAMP_REFLECTOR ? 'full' : 'light';
      config[TwampReflectorConfigMapping.ADDRESS] = this.getTestParamsById(
        'source-ip',
        this.testConfig
      );
      config[TwampReflectorConfigMapping.SUBNET] = this.getTestParamsById(
        'subnet',
        this.testConfig
      );
      config[TwampReflectorConfigMapping.GATEWAY] = this.getTestParamsById(
        'gateway',
        this.testConfig
      );
      const persist = this.getTestParamsById('persist', this.testConfig);
      config[TwampReflectorConfigMapping.REBOOT] = persist?.toString() === 'true' ? true : false;
      config[TwampReflectorConfigMapping.UDP_PORT] =
        this.getTestParamsById('reflector-udp-port', this.testConfig) || 50000;

      const vlanId = this.getTestParamsById('vlan-id', this.testConfig);
      const cVlanId = this.getTestParamsById('inner-vlan-id', this.testConfig);
      if (vlanId) {
        config[TwampReflectorConfigMapping.SVLAN_ID] = vlanId;
        if (cVlanId) {
          config[TwampReflectorConfigMapping.CVLAN_ID] = cVlanId;
          config[TwampReflectorConfigMapping.ETHER_TYPE] = this.getTestParamsById(
            'tpid',
            this.testConfig
          );
        }
      }
      config[TwampReflectorConfigMapping.VLAN_CONFIG] =
        vlanId && cVlanId
          ? vlanConfigValue.Q_IN_Q
          : vlanId
          ? vlanConfigValue.SINGLE_VLAN
          : vlanConfigValue.UNTAGGED;
      if (this.getTestParamsById('prefix-length', this.testConfig)) {
        //ipv6 load config
        config[TwampReflectorConfigMapping.ADDRESS_IPV6] = this.getTestParamsById(
          'source-ip',
          this.testConfig
        );
        config[TwampReflectorConfigMapping.GATEWAY_V6] = this.getTestParamsById(
          'gateway',
          this.testConfig
        );
        config[TwampReflectorConfigMapping.PREFIX_V6] = this.getTestParamsById(
          'prefix-length',
          this.testConfig
        );
        config[TwampReflectorConfigMapping.IP_VERSION] = 'v6';
      } else {
        config[TwampReflectorConfigMapping.IP_VERSION] = 'v4';
      }
      config[TwampReflectorConfigMapping.SESSIONS] =
        this.getTestParamsById('max-connections', this.testConfig) || 5;
      config[TwampReflectorConfigMapping.FLOWS] =
        this.getTestParamsById('max-flows', this.testConfig) || 50;
      const twampString = this.getTestParamsById('twamp', this.testConfig);
      if (twampString) {
        const twampObj = JSON.parse(twampString);
        config[TwampReflectorConfigMapping.CONTROL_PORT] =
          twampObj['ietf-twamp:twamp']['twamp-server']['server-tcp-port'];
        config[TwampReflectorConfigMapping.SESSIONS] =
          config[TwampReflectorConfigMapping.SESSIONS] ||
          twampObj['ietf-twamp:twamp']['twamp-server']['max-connections'];
        config[TwampReflectorConfigMapping.FLOWS] =
          config[TwampReflectorConfigMapping.FLOWS] ||
          twampObj['ietf-twamp:twamp']['twamp-server']['max-flows'];
      }
    }
    config = { ...config, ...commonParams };
    return config;
  }

  //eslint-disable-next-line
  getY1731OutputParams(testParams: any): any {
    const masterData = testParams['soam-pm-viavi:y1731']['soam-pm-config'];
    const config = {};
    config[Y1731ConfigFieldMapping.SRC_MAC_ADDRESS] = masterData['mac-address'];
    config[Y1731ConfigFieldMapping.MEP_ID] = masterData['mep-id'];
    config[Y1731ConfigFieldMapping.FLOWS] = masterData['flow'];
    config[Y1731ConfigFieldMapping.DESTINATION_MAC] = masterData['flow'][0]['remote-mac-addr'];
    config[Y1731ConfigFieldMapping.FLOW_ID] = masterData['flow'][0]['flow-id'];
    config[Y1731ConfigFieldMapping.PM_REPORT_INTERVAL] = this.getPeriodicInterval(this.testConfig);
    const persist = this.getTestParamsById('persist', this.testConfig);
    config[Y1731ConfigFieldMapping.PERSIST_AFTER_REBOOT] = persist === 'true' ? true : false;
    const commonParams = this.getCommonAgentTestparams();
    const vlanId = this.getTestParamsById('vlan-id', this.testConfig);
    const cVlanId = this.getTestParamsById('inner-vlan-id', this.testConfig);
    if (vlanId) {
      config[Y1731ConfigFieldMapping.SVLAN_ID] = vlanId;
      config[Y1731ConfigFieldMapping.SVLAN_PRIORITY] =
        this.getTestParamsById('vlan-priority', this.testConfig) || '0';
      if (cVlanId) {
        config[Y1731ConfigFieldMapping.CVLAN_ID] = cVlanId;
        config[Y1731ConfigFieldMapping.CVLAN_PRIORITY] =
          this.getTestParamsById('inner-vlan-priority', this.testConfig) || '0';
        config[Y1731ConfigFieldMapping.CVLAN_ETHER_TYPE] = this.getTestParamsById(
          'tpid',
          this.testConfig
        );
      }
    }

    config[Y1731ConfigFieldMapping.VLAN_CONFIG] =
      vlanId && cVlanId
        ? vlanConfigValue.Q_IN_Q
        : vlanId
        ? vlanConfigValue.SINGLE_VLAN
        : vlanConfigValue.UNTAGGED;
    return { ...commonParams, ...config };
  }

  //eslint-disable-next-line
  getLayerType(masterData: any): string {
    if (masterData['layer2'] && Object.keys(masterData['layer2']).length) {
      return TestLayerValue.L2;
    } else {
      return TestLayerValue.L3;
    }
  }

  getIpAddressType(ipAddress: string): string {
    if (CommonRegEx.IPV6.test(ipAddress)) {
      return IPAddressValue.IP_V6;
    } else {
      return IPAddressValue.IP_V4;
    }
  }

  //eslint-disable-next-line
  getRfc2544OutputParams(testParams: any): any {
    const masterData = testParams['rfc2544-viavi']['config'];
    const stream =
      this.testConfig?.config?.test?.['extended-config']?.pluginConfig?.connections?.connections[0]
        ?.streams[0];
    const config = {};
    config[RFC2544ConfigFieldMapping.STREAM] = {};
    config[RFC2544ConfigFieldMapping.TEST_MODE] = TestMode.LOOPBACK;
    config[RFC2544ConfigFieldMapping.LAYER_TO_TEST] = this.getLayerType(masterData);
    config[RFC2544ConfigFieldMapping.IP_ADDRESS_TYPE] = this.getIpAddressType(
      this.getTestParamsById('source-ip', this.testConfig)
    );
    config[RFC2544ConfigFieldMapping.SUPPRESS_LOOP_COMMANDS] =
      masterData['loopback-device'] === 'other';
    config[RFC2544ConfigFieldMapping.SCHEDULER] = this.getScheduledParams(this.testConfig);
    const commonParams = this.getCommonAgentTestparams();
    if (config[RFC2544ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L2) {
      config[RFC2544ConfigFieldMapping.DESTINATION_MAC] = masterData['layer2']['destination-mac'];
      config[RFC2544ConfigFieldMapping.STREAM].dscp = Number(masterData['layer2']['tos-or-dscp']);
      config[RFC2544ConfigFieldMapping.STREAM]['tos-diff-serv'] =
        this.testConfig['config']['test']?.['extended-config']?.['tos-diffServ']?.value ||
        stream?.strmVerifyTos === 'true'
          ? true
          : false;
      if (config[RFC2544ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4) {
        config[RFC2544ConfigFieldMapping.SRC_IPV4_ADDRESS] = this.getTestParamsById(
          'source-ip',
          this.testConfig
        );
        config[RFC2544ConfigFieldMapping.DEST_IPV4_ADDRESS] =
          masterData['layer2']['destination-ip'];
      } else if (config[RFC2544ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V6) {
        const ipv6StreamConfig =
          this.testConfig?.config?.['test']?.['extended-config']?.['ipv6-stream-info'];
        config[RFC2544ConfigFieldMapping.SRC_IPV6_ADDRESS] = this.getTestParamsById(
          'source-ip',
          this.testConfig
        );
        config[RFC2544ConfigFieldMapping.DEST_IPV6_ADDRESS] =
          masterData['layer2']['destination-ip'];
        config[RFC2544ConfigFieldMapping.STREAM]['flow-label'] =
          ipv6StreamConfig?.['flow-label'] || stream?.strmIpv6FlowLabel;
        config[RFC2544ConfigFieldMapping.STREAM]['traffic-class'] =
          ipv6StreamConfig?.['traffic-class'] || stream?.strmIpv6TrafficClass;
        config[RFC2544ConfigFieldMapping.STREAM]['hop-limit'] =
          ipv6StreamConfig?.['hop-limit'] || stream?.strmIpv6HopLimit;
      }
    } else if (config[RFC2544ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L3) {
      if (config[RFC2544ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4) {
        config[RFC2544ConfigFieldMapping.STREAM].dscp = Number(
          masterData['layer3-ipv4']['tos-or-dscp']
        );
        config[RFC2544ConfigFieldMapping.DEST_IPV4_ADDRESS] =
          masterData['layer3-ipv4']['destination-ip'];
        config[RFC2544ConfigFieldMapping.STREAM]['tos-diff-serv'] =
          masterData['layer3-ipv4']['filter-on-tos'] === 'true' ? true : false;
        config[RFC2544ConfigFieldMapping.SRC_IPV4_ADDRESS] = this.getTestParamsById(
          'source-ip',
          this.testConfig
        );
        config[RFC2544ConfigFieldMapping.SRC_SUBNET] = this.getTestParamsById(
          'subnet',
          this.testConfig
        );
        config[RFC2544ConfigFieldMapping.SRC_GATEWAY] = this.getTestParamsById(
          'gateway',
          this.testConfig
        );
      } else if (config[RFC2544ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V6) {
        config[RFC2544ConfigFieldMapping.DEST_IPV6_ADDRESS] =
          masterData['layer3-ipv6']['destination-ip'];
        config[RFC2544ConfigFieldMapping.SRC_IPV6_ADDRESS] = this.getTestParamsById(
          'source-ip',
          this.testConfig
        );
        config[RFC2544ConfigFieldMapping.SRC_SUBNET] = this.getTestParamsById(
          'prefix-length',
          this.testConfig
        );
        config[RFC2544ConfigFieldMapping.SRC_GATEWAY] = this.getTestParamsById(
          'gateway',
          this.testConfig
        );
        config[RFC2544ConfigFieldMapping.STREAM]['flow-label'] =
          masterData['layer3-ipv6']?.['flow-label'];
        config[RFC2544ConfigFieldMapping.STREAM]['traffic-class'] =
          masterData['layer3-ipv6']?.['traffic-class'];
        config[RFC2544ConfigFieldMapping.STREAM]['hop-limit'] =
          masterData['layer3-ipv6']?.['hop-limit'];
      }
    }
    config[RFC2544ConfigFieldMapping.STREAM_RATE_CONVENTION] = masterData['unit-convention'];
    config[RFC2544ConfigFieldMapping.MAX_TEST_DURATION] = masterData['test-timeout'];
    config[RFC2544ConfigFieldMapping.STREAM]['frame-size-list'] = this.getRFCFrameSize(masterData);
    config[RFC2544ConfigFieldMapping.STREAM]['cir-from-engine-a'] = masterData['cir'] / 1000;
    config[RFC2544ConfigFieldMapping.NETWORK_OVERHEAD] = Number(masterData['network-overhead']);
    if (masterData['throughput']) {
      const throughputData = masterData['throughput'];
      config[RFC2544ConfigFieldMapping.THROUGHPUT] = true;
      config[RFC2544ConfigFieldMapping.THROUGHTPUT_STOP_OVERALL_TEST_ON_FAILURE] =
        throughputData['stop-on-throughput-fail'] === 'true' ? true : false;
      config[RFC2544ConfigFieldMapping.THROUGHPUT_DURATION] = Number(throughputData['duration']);
      config[RFC2544ConfigFieldMapping.THROUGHPUT_BANDWIDTH] = Number(
        throughputData['bw-accuracy']
      );
      config[RFC2544ConfigFieldMapping.THROUGHPUT_FRAME_LOSS_TOLERANCE] = Number(
        throughputData['max-frame-loss']
      );
      config[RFC2544ConfigFieldMapping.THROUGHPUT_STARTING_UTILIZATION] = Number(
        throughputData['start-utilization']
      );
      config[RFC2544ConfigFieldMapping.THROUGHPUT_MAX_UTILIZATION] = Number(
        throughputData['success-utilization-high']
      );
      config[RFC2544ConfigFieldMapping.THROUGHPUT_MIN_UTILIZATION] = Number(
        throughputData['success-utilization-low']
      );
    } else {
      config[RFC2544ConfigFieldMapping.THROUGHPUT] = false;
    }
    if (masterData['latency-fdv']) {
      const latencyData = masterData['latency-fdv'];
      config[RFC2544ConfigFieldMapping.LATENCY] = true;
      config[RFC2544ConfigFieldMapping.LATENCY_NUM_OF_TRIALS] = Number(latencyData['trials']);
      config[RFC2544ConfigFieldMapping.LATENCY_DURATION] = Number(latencyData['duration']);
      config[RFC2544ConfigFieldMapping.TARGET_LATENCY] = Number(latencyData['latency-max']);
      config[RFC2544ConfigFieldMapping.TARGET_FDV] = Number(latencyData['fdv-max']);
      config[RFC2544ConfigFieldMapping.LATENCY_UTILIZATION] = Number(
        latencyData['latency-utilization-percent']
      );
    } else {
      config[RFC2544ConfigFieldMapping.LATENCY] = false;
    }
    if (masterData['frameloss']) {
      const framelossData = masterData['frameloss'];
      config[RFC2544ConfigFieldMapping.FRAME_LOSS] = true;
      config[RFC2544ConfigFieldMapping.FRAME_LOSS_RUN_THORUGHPUT_FAILS] =
        framelossData['skip-if-throughput-pass'] === 'true' ? true : false;
      config[RFC2544ConfigFieldMapping.FRAME_LOSS_DURATION] = Number(framelossData['duration']);
      config[RFC2544ConfigFieldMapping.FRAME_LOSS_MAX_LOSS_ALLOWED] = Number(
        framelossData['max-percent']
      );
      config[RFC2544ConfigFieldMapping.FRAME_LOSS_REDUCE_UTILIZATION] = Number(
        framelossData['utilization-granularity']
      );
    } else {
      config[RFC2544ConfigFieldMapping.FRAME_LOSS] = false;
    }
    if (masterData['bb-cbs']) {
      const b2bCbsData = masterData['bb-cbs'];
      config[RFC2544ConfigFieldMapping.BACK_TO_BACK] = true;
      if (b2bCbsData['bb']) {
        config[RFC2544ConfigFieldMapping.BACK_TO_BACK_TEST_TYPE] = 'b2b';
        config[RFC2544ConfigFieldMapping.BACK_TO_BACK_REPETITIONS] = Number(
          b2bCbsData['bb']['trials']
        );
        config[RFC2544ConfigFieldMapping.BACK_TO_BACK_TARGET_BURST_DURATION] = Number(
          b2bCbsData['bb']['burst-duration']
        );
        config[RFC2544ConfigFieldMapping.BACK_TO_BACK_MAX_TIME_RECEIVE_BURST] = Number(
          b2bCbsData['bb']['receive-time']
        );
        config[RFC2544ConfigFieldMapping.BACK_TO_BACK_BURST_RESOLUTION] = Number(
          b2bCbsData['bb']['granularity']
        );
      } else if (b2bCbsData['cbs']) {
        config[RFC2544ConfigFieldMapping.BACK_TO_BACK_TEST_TYPE] = 'cbs';
        config[RFC2544ConfigFieldMapping.BACK_TO_BACK_REPETITIONS] = Number(
          b2bCbsData['cbs']['trials']
        );
        config[RFC2544ConfigFieldMapping.CBS_COMMITTED_BURST_SIZE] = Number(
          b2bCbsData['cbs']['burst-size']
        );
        config[RFC2544ConfigFieldMapping.CBS_KILO_BYTE_SIZE] = Number(
          b2bCbsData['cbs']['burst-size-multiplier']
        );
      }
    } else {
      config[RFC2544ConfigFieldMapping.BACK_TO_BACK] = false;
    }

    //Vlans
    const vlanId = this.getTestParamsById('vlan-id', this.testConfig);
    const cVlanId = this.getTestParamsById('inner-vlan-id', this.testConfig);
    if (vlanId) {
      config[RFC2544ConfigFieldMapping.SVLAN_ID] = vlanId;
      config[RFC2544ConfigFieldMapping.SVLAN_PRIORITY] =
        this.getTestParamsById('vlan-priority', this.testConfig) || '0';
      if (cVlanId) {
        config[RFC2544ConfigFieldMapping.CVLAN_ID] = cVlanId;
        config[RFC2544ConfigFieldMapping.CVLAN_PRIORITY] =
          this.getTestParamsById('inner-vlan-priority', this.testConfig) || '0';
        // config[RFC2544ConfigFieldMapping.CVLAN_ETHER_TYPE] =
        //   this.getTestParamsById('tpid', this.testConfig);
      }
    }

    config[RFC2544ConfigFieldMapping.VLAN_CONFIG] =
      vlanId && cVlanId
        ? vlanConfigValue.Q_IN_Q
        : vlanId
        ? vlanConfigValue.SINGLE_VLAN
        : vlanConfigValue.UNTAGGED;
    return { ...commonParams, ...config };
  }

  //eslint-disable-next-line
  getRFCFrameSize(masterData: any): string {
    let selectedFrames = '';
    const frameList = masterData['frame-size-info'];
    Object.keys(frameList).forEach(key => {
      if (frameList[key] === 'true') {
        const size = key.split('use-frame-size-');
        selectedFrames = selectedFrames + size[1] + ',';
      } else if (key === 'custom-frame-size') {
        selectedFrames = selectedFrames + 'custom:' + frameList[key] + ',';
      }
    });
    return selectedFrames;
  }

  getTwampOutputParams(): any {
    const configParms = this.twampTestConfig.getTwampOutputParams(this.testConfig);
    const commonParams = this.getCommonAgentTestparams();
    const originatorId = this.selectedTestEntity.getOriginatorAgent()?.deviceData?.id;
    const destinationId = this.selectedTestEntity.getDestinationAgent()?.deviceData?.id;
    if (originatorId) {
      const orginatorDevice = this.deviceService.getDeviceById(originatorId);
      commonParams[TwampConfigFieldMapping.SELECTED_ORIGINATOR] = orginatorDevice;
      commonParams[TwampConfigFieldMapping.AGENT_TYPE] = orginatorDevice.getModel();
    }
    if (destinationId) {
      const destinationDevice = this.deviceService.getDeviceById(destinationId);
      commonParams[TwampConfigFieldMapping.SELECTED_DESTINATION] = destinationDevice;
    }
    return { ...commonParams, ...configParms };
  }

  getMultistepOutputParams(): any {
    let config = {};
    config[MultistepConfigMapping.RUN_TYPE] = this.selectedTestEntity.getTestListRunType();
    config[MultistepConfigMapping.SCRIPT_NAME] =
      this.selectedTestEntity.getTestListData()?.scriptName;
    config[MultistepConfigMapping.INTERVAL] = this.selectedTestEntity.getTestListData()?.interval;
    config[MultistepConfigMapping.SELECTED_APP_ID] =
      this.selectedTestEntity.getDestinationApplicationId();
    config[MultistepConfigMapping.PROXY_CONFIG] =
      this.selectedTestEntity.getTestListData()?.proxyConfig;
    const commonParams = this.getCommonAgentTestparams();
    const originatorId = this.selectedTestEntity.getOriginatorAgent()?.deviceData?.id;
    const orginatorDevice = this.deviceService.getDeviceById(originatorId);
    commonParams[MultistepConfigMapping.SELECTED_ORIGINATOR] = [orginatorDevice];
    config = { ...config, ...commonParams };
    return config;
  }

  getServiceOutputParams(): any {
    let config = {};
    config[ServiceConfigFieldMapping.RUN_TYPE] = this.selectedTestEntity.getTestListRunType();
    config[ServiceConfigFieldMapping.SELECTED_APP_ID] =
      this.selectedTestEntity.getDestinationApplicationId();
    config[ServiceConfigFieldMapping.PROXY_CONFIG] =
      this.selectedTestEntity.getTestListData()?.proxyConfig;
    const thresholdsObj = this.selectedTestEntity.getTestListData();
    const mosThresholds = thresholdsObj?.['severityThresholds'];
    if (mosThresholds) {
      config[ServiceConfigFieldMapping.THRESHOLD_CRITICALL] = mosThresholds[0].threshold;
      config[ServiceConfigFieldMapping.THRESHOLD_WARNING] = mosThresholds[1].threshold;
    }
    const commonParams = this.getCommonAgentTestparams();
    const originatorId = this.selectedTestEntity.getOriginatorAgent()?.deviceData?.id;
    const orginatorDevice = this.deviceService.getDeviceById(originatorId);
    commonParams[ServiceConfigFieldMapping.SELECTED_ORIGINATOR] = [orginatorDevice];
    config = { ...config, ...commonParams };
    return config;
  }

  getVoipOutputParams(): any {
    let config = {};
    config[VoipConfigMapping.RUN_TYPE] = this.selectedTestEntity.getTestListRunType();
    config[VoipConfigMapping.PROXY_CONFIG] = this.selectedTestEntity.getTestListData()?.proxyConfig;
    const thresholdsObj = this.selectedTestEntity.getTestListData();
    const mosThresholds = thresholdsObj?.['threshold'];
    if (mosThresholds) {
      config[VoipConfigMapping.THRESHOLD_CRITICALL] = mosThresholds.critical;
      config[VoipConfigMapping.THRESHOLD_WARNING] = mosThresholds.warning;
    }
    const commonParams = this.getCommonAgentTestparams();

    const originatorId = this.selectedTestEntity.getOriginatorAgent()?.deviceData?.id;
    const orginatorDevice = this.deviceService.getDeviceById(originatorId);
    commonParams[VoipConfigMapping.SELECTED_ORIGINATOR] = orginatorDevice;

    const destinationId = this.selectedTestEntity.getDestinationAgent()?.deviceData?.id;
    const destinationDevice = this.deviceService.getDeviceById(destinationId);
    commonParams[VoipConfigMapping.SELECTED_DESTINATION] = destinationDevice;

    config = { ...config, ...commonParams };
    return config;
  }

  //eslint-disable-next-line
  getHttpOutputParams(testParams: any): any {
    let config = {};
    const masterData = testParams['viavi-solutions-http:http'];
    const httpTestParams = masterData['http-parameters'];
    config[HttpConfigFieldMapping.ENGINE_A_INFO] = masterData['engine-a-info'];
    config[HttpConfigFieldMapping.ENGINE_B_INFO] = masterData['engine-b-info'];
    const originatorId = this.selectedTestEntity.getOriginatorAgent()?.deviceData?.id;
    const destinationId = this.selectedTestEntity.getDestinationAgent()?.deviceData?.id;
    config[HttpConfigFieldMapping.TEST_MODE] = httpTestParams[HttpConfigFieldMapping.TEST_MODE];
    config[HttpConfigFieldMapping.DIRECTION] = httpTestParams[HttpConfigFieldMapping.DIRECTION];
    config[HttpConfigFieldMapping.THRESHOLD_ENABLED] =
      httpTestParams[HttpConfigFieldMapping.THRESHOLD_ENABLED];
    config[HttpConfigFieldMapping.TARGET_ENABLED] =
      httpTestParams[HttpConfigFieldMapping.TARGET_ENABLED];
    config[HttpConfigFieldMapping.ENGINE_A_TO_B_URL] =
      httpTestParams[HttpConfigFieldMapping.ENGINE_A_TO_B_URL];
    config[HttpConfigFieldMapping.ENGINE_B_TO_A_URL] =
      httpTestParams[HttpConfigFieldMapping.ENGINE_B_TO_A_URL];
    config[HttpConfigFieldMapping.TEST_TYPE] = httpTestParams[HttpConfigFieldMapping.TEST_TYPE];
    config[HttpConfigFieldMapping.TCP_PORT] = httpTestParams[HttpConfigFieldMapping.TCP_PORT];
    config[HttpConfigFieldMapping.RUN_TYPE] = httpTestParams[HttpConfigFieldMapping.RUN_TYPE];
    const commonParams = this.getCommonAgentTestparams();
    if (originatorId) {
      const orginatorDevice = this.deviceService.getDeviceById(originatorId);
      commonParams[HttpConfigFieldMapping.SELECTED_ORIGINATOR] = orginatorDevice;
    }
    if (destinationId) {
      const destinationDevice = this.deviceService.getDeviceById(destinationId);
      commonParams[HttpConfigFieldMapping.SELECTED_DESTINATION] = destinationDevice;
    }
    config = { ...config, ...commonParams, ...masterData['http-parameters'] };
    config[HttpConfigFieldMapping.ENGINE_A_TO_B_THRESHOLD] =
      httpTestParams[HttpConfigFieldMapping.ENGINE_A_TO_B_THRESHOLD] || 90;
    config[HttpConfigFieldMapping.ENGINE_B_TO_A_THRESHOLD] =
      httpTestParams[HttpConfigFieldMapping.ENGINE_B_TO_A_THRESHOLD] || 90;
    config[HttpConfigFieldMapping.ENGINE_A_TO_B_TARGET] =
      httpTestParams[HttpConfigFieldMapping.ENGINE_A_TO_B_TARGET] || 100;
    config[HttpConfigFieldMapping.ENGINE_B_TO_A_TARGET] =
      httpTestParams[HttpConfigFieldMapping.ENGINE_B_TO_A_TARGET] || 100;
    config[HttpConfigFieldMapping.SCHEDULER] = this.getScheduledParams(this.testConfig);
    return config;
  }

  //eslint-disable-next-line
  getFtpOutputParams(testParams: any): any {
    let config = {};
    const masterData = testParams['viavi-solutions-ftp:ftp'];
    const ftpTestParams = masterData['ftp-parameters'];
    config[FtpConfigFieldMapping.ENGINE_A_INFO] = masterData['engine-a-info'];
    config[FtpConfigFieldMapping.ENGINE_B_INFO] = masterData['engine-b-info'];
    const originatorId = this.selectedTestEntity.getOriginatorAgent()?.deviceData?.id;
    if (originatorId) {
      const orginatorDevice = this.deviceService.getDeviceById(originatorId);
      config[FtpConfigFieldMapping.SELECTED_ORIGINATOR] = orginatorDevice;
    }
    const destinationId = this.selectedTestEntity.getDestinationAgent()?.deviceData?.id;
    config[FtpConfigFieldMapping.TEST_MODE] = ftpTestParams[FtpConfigFieldMapping.TEST_MODE];
    config[FtpConfigFieldMapping.DIRECTION] = ftpTestParams[FtpConfigFieldMapping.DIRECTION];
    config[FtpConfigFieldMapping.THRESHOLD_ENABLED] =
      ftpTestParams[FtpConfigFieldMapping.THRESHOLD_ENABLED];
    config[FtpConfigFieldMapping.TARGET_ENABLED] =
      ftpTestParams[FtpConfigFieldMapping.TARGET_ENABLED];
    config[FtpConfigFieldMapping.ENGINE_A_TO_B_URL] =
      ftpTestParams[FtpConfigFieldMapping.ENGINE_A_TO_B_URL];
    config[FtpConfigFieldMapping.ENGINE_B_TO_A_URL] =
      ftpTestParams[FtpConfigFieldMapping.ENGINE_B_TO_A_URL];
    config[FtpConfigFieldMapping.TEST_TYPE] = ftpTestParams[FtpConfigFieldMapping.TEST_TYPE];
    config[FtpConfigFieldMapping.TCP_PORT] = ftpTestParams[FtpConfigFieldMapping.TCP_PORT];
    config[FtpConfigFieldMapping.RUN_TYPE] = ftpTestParams[FtpConfigFieldMapping.RUN_TYPE];
    const commonParams = this.getCommonAgentTestparams();
    if (originatorId) {
      const orginatorDevice = this.deviceService.getDeviceById(originatorId);
      commonParams[FtpConfigFieldMapping.SELECTED_ORIGINATOR] = orginatorDevice;
    }
    if (destinationId) {
      const destinationDevice = this.deviceService.getDeviceById(destinationId);
      commonParams[FtpConfigFieldMapping.SELECTED_DESTINATION] = destinationDevice;
    }
    config = { ...config, ...commonParams, ...masterData['ftp-parameters'] };
    config[FtpConfigFieldMapping.ENGINE_A_TO_B_THRESHOLD] =
      ftpTestParams[FtpConfigFieldMapping.ENGINE_A_TO_B_THRESHOLD] || 90;
    config[FtpConfigFieldMapping.ENGINE_B_TO_A_THRESHOLD] =
      ftpTestParams[FtpConfigFieldMapping.ENGINE_B_TO_A_THRESHOLD] || 90;
    config[FtpConfigFieldMapping.ENGINE_A_TO_B_TARGET] =
      ftpTestParams[FtpConfigFieldMapping.ENGINE_A_TO_B_TARGET] || 100;
    config[FtpConfigFieldMapping.ENGINE_B_TO_A_TARGET] =
      ftpTestParams[FtpConfigFieldMapping.ENGINE_B_TO_A_TARGET] || 100;
    config[FtpConfigFieldMapping.SCHEDULER] = this.getScheduledParams(this.testConfig);
    return config;
  }

  //eslint-disable-next-line
  getTruespeedOutputParams(testParams: any): any {
    let config = {};
    const masterData = testParams['truespeed:truespeed'];
    config[TruespeedConfigFieldMapping.ENGINE_A_INFO] = masterData['engine-a-info'];
    config[TruespeedConfigFieldMapping.ENGINE_B_INFO] = masterData['engine-b-info'];
    //config[ConfigFieldMapping.SRC_AGENT_TYPE] = masterData['engine-a-info']?.['engine-type'];
    config[TruespeedConfigFieldMapping.SRC_ASSIGNED_IP_ADDRESS] =
      masterData['engine-a-info']?.['address'];
    config[TruespeedConfigFieldMapping.SRC_TEST_IP_ADDRESS] =
      masterData['engine-a-info']?.['test-ip'];
    config[TruespeedConfigFieldMapping.SRC_SUBNET] =
      masterData['engine-a-info']?.['test-subnet-mask'];
    config[TruespeedConfigFieldMapping.SRC_GATEWAY] = masterData['engine-a-info']?.['test-gateway'];
    config[TruespeedConfigFieldMapping.SRC_TRANSMIT_INTERFACE] =
      masterData['engine-a-info']?.['tx-interface'];
    config[TruespeedConfigFieldMapping.SRC_VLAN_ID] = masterData['engine-a-info']?.['test-vlan-id'];

    config[TruespeedConfigFieldMapping.DEST_AGENT_TYPE] =
      masterData['engine-b-info']?.['engine-type'];
    config[TruespeedConfigFieldMapping.DEST_ASSIGNED_IP_ADDRESS] =
      masterData['engine-b-info']?.['address'];
    config[TruespeedConfigFieldMapping.DEST_TEST_IP_ADDRESS] =
      masterData['engine-b-info']?.['test-ip'];
    config[TruespeedConfigFieldMapping.DEST_SUBNET] =
      masterData['engine-b-info']?.['test-subnet-mask'];
    config[TruespeedConfigFieldMapping.DEST_GATEWAY] =
      masterData['engine-b-info']?.['test-gateway'];
    config[TruespeedConfigFieldMapping.DEST_TRANSMIT_INTERFACE] =
      masterData['engine-b-info']?.['tx-interface'];
    config[TruespeedConfigFieldMapping.DEST_VLAN_ID] =
      masterData['engine-b-info']?.['test-vlan-id'];
    config[TruespeedConfigFieldMapping.EXPIRATION_DATE] =
      this.testConfig.config.test?.controller?.['expiration-date'] || null;
    config[TruespeedConfigFieldMapping.MAX_EXECUTION_COUNTS] =
      this.testConfig.config.test?.controller?.['max-exec-count'] || null;
    config[TruespeedConfigFieldMapping.SHA_ID] = this.testConfig.sha;
    config[TruespeedConfigFieldMapping.SCHEDULER] = this.getScheduledParams(this.testConfig);
    const originatorId = this.selectedTestEntity.getOriginatorAgent()?.deviceData?.id;
    const destinationId = this.selectedTestEntity.getDestinationAgent()?.deviceData?.id;
    const commonParams = this.getCommonAgentTestparams();
    if (originatorId) {
      const orginatorDevice = this.deviceService.getDeviceById(originatorId);
      commonParams[HttpConfigFieldMapping.SELECTED_ORIGINATOR] = orginatorDevice;
      commonParams[TruespeedConfigFieldMapping.AGENT_TYPE] = orginatorDevice.getModel();
    }
    if (destinationId) {
      const destinationDevice = this.deviceService.getDeviceById(destinationId);
      commonParams[HttpConfigFieldMapping.SELECTED_DESTINATION] = destinationDevice;
    }
    if (this.testConfig['config']['test']?.['extended-config']) {
      config[TruespeedConfigFieldMapping.TEST_MODE] =
        this.testConfig['config']['test']?.['extended-config']?.test?.mode;
    } else {
      config[TruespeedConfigFieldMapping.TEST_MODE] = this.getTruespeedTestMode(config);
    }

    config = { ...config, ...commonParams, ...masterData['truespeed-parameters'] };
    return config;
  }

  //eslint-disable-next-line
  getTruespeedTestMode(config): string {
    const truespeedConfig = this.getTestParams('truespeed', this.testConfig);
    if (this.testConfig['config']['agent']?.['secondary']) {
      return TestMode.MA_TO_MA;
    } else if (
      truespeedConfig?.['truespeed:truespeed']?.['truespeed-parameters']?.['destination-ip']
    ) {
      return TestMode.MA_TO_JMEP;
    } else {
      return TestMode.CLIENT_TO_MA;
    }
  }

  //eslint-disable-next-line
  getY1564OutputParams(testParams: any): any {
    let config = {};
    const masterData = testParams['y1564:y1564'] || testParams['viavi-solutions-y1564:y1564'];
    config[Y1564ConfigFieldMapping.TEST_MODE] =
      this.testConfig['config']['test']?.['extended-config']?.test?.mode;
    config[Y1564ConfigFieldMapping.ENGINE_A_INFO] = masterData['engine-a-info'];
    config[Y1564ConfigFieldMapping.ENGINE_B_INFO] = masterData['engine-b-info'];
    config[Y1564ConfigFieldMapping.SRC_IPV4_ADDRESS] = masterData['engine-a-info']?.['test-ip'];
    config[Y1564ConfigFieldMapping.SRC_IPV6_ADDRESS] = masterData['engine-a-info']?.['test-ip'];
    config[Y1564ConfigFieldMapping.SRC_SUBNET] = masterData['engine-a-info']?.['test-subnet-mask'];
    config[Y1564ConfigFieldMapping.SRC_GATEWAY] = masterData['engine-a-info']?.['test-gateway'];
    config[Y1564ConfigFieldMapping.DEST_IPV4_ADDRESS] = masterData['engine-b-info']?.['test-ip'];
    config[Y1564ConfigFieldMapping.DEST_SUBNET] = masterData['engine-b-info']?.['test-subnet-mask'];
    config[Y1564ConfigFieldMapping.DEST_GATEWAY] = masterData['engine-b-info']?.['test-gateway'];
    config[Y1564ConfigFieldMapping.SCHEDULER] = this.getScheduledParams(this.testConfig);
    config[Y1564ConfigFieldMapping.DEVICE_SELECTION_METHOD] =
      masterData['group'][0][Y1564ConfigFieldMapping.DEVICE_SELECTION_METHOD];
    config[Y1564ConfigFieldMapping.STREAM_NAME] =
      masterData['group'][0][Y1564ConfigFieldMapping.STREAM_NAME];
    config[Y1564ConfigFieldMapping.STREAMS] = this.getFlatternedStreamData(
      masterData['group'][0][Y1564ConfigFieldMapping.STREAMS],
      masterData['engine-a-info']['engine-type']
    );
    const streamsConfig = {
      ...config[Y1564ConfigFieldMapping.STREAMS][0]['l2-config'],
      ...config[Y1564ConfigFieldMapping.STREAMS][0]['l3-ipv4-config'],
      ...config[Y1564ConfigFieldMapping.STREAMS][0]['l3-ipv6-config']
    };
    const commonParams = this.getCommonAgentTestparams();
    config[Y1564ConfigFieldMapping.DEFINED_ADDRESS] = this.getY1564ExtendedPluginConfig(
      masterData['y1564-parameters']
    );
    const destinationAgentType =
      commonParams[Y1564ConfigFieldMapping.SELECTED_DESTINATION]?.getModel();
    const isDestinationAgentMts = this.deviceService?.isMtsAgentModel(destinationAgentType);
    if (isDestinationAgentMts) {
      commonParams[Y1564ConfigFieldMapping.AGENT_TYPE] = destinationAgentType;
    }
    config[Y1564ConfigFieldMapping.VLAN_CONFIG] = streamsConfig[Y1564ConfigFieldMapping.CVLAN_ID]
      ? vlanConfigValue.Q_IN_Q
      : streamsConfig[Y1564ConfigFieldMapping.SVLAN_ID]
      ? vlanConfigValue.SINGLE_VLAN
      : vlanConfigValue.UNTAGGED;
    config = {
      ...config,
      ...commonParams,
      ...streamsConfig,
      ...masterData['y1564-parameters']
    };
    const lmapObj = this.testConfig?.config?.config?.[0]?.primaryConfig;
    const schedule = this.lmap.extractScheduleByTaskName(lmapObj, 'y1564');
    const y1564Option = schedule?.action?.[0]?.option?.find((opt: any) => opt.id === 'y1564');
    const y1564Value = y1564Option ? JSON.parse(y1564Option.value) : null;
    const configStreams =
      (y1564Value?.['viavi-solutions-y1564:y1564'] || y1564Value?.['y1564:y1564'])?.group?.[0]
        ?.streams || [];
    const disableStreamAvailable = !configStreams.every(
      stream => stream['stream-in-use'] === 'true' || stream['stream-in-use'] === undefined
    );
    if (disableStreamAvailable) {
      const optionsStreams = this.getY1564StreamsFromOptionsPluginConfig(
        masterData['engine-a-info']['engine-type']
      );
      if (optionsStreams) {
        config[Y1564ConfigFieldMapping.STREAMS] = optionsStreams;
      }
    }
    if (
      config[Y1564ConfigFieldMapping.STEP_PERCENTS] === undefined ||
      config[Y1564ConfigFieldMapping.STEP_PERCENTS] === null
    ) {
      config[Y1564ConfigFieldMapping.STEP_PERCENTS] = [];
    }
    return config;
  }

  getY1564StreamsFromOptionsPluginConfig(agentType: string): any {
    const lmapObj = this.testConfig?.config?.config?.[0]?.primaryConfig;
    const schedule = this.lmap.extractScheduleByTaskName(lmapObj, 'y1564');
    const y1564Option = schedule?.action?.[0]?.option?.find((opt: any) => opt.id === 'y1564');
    const y1564Value = y1564Option ? JSON.parse(y1564Option.value) : null;
    const configStreams =
      (y1564Value?.['viavi-solutions-y1564:y1564'] || y1564Value?.['y1564:y1564'])?.group?.[0]
        ?.streams || [];
    let streamData = [];
    let streamObj = {};
    if (configStreams?.length > 0) {
      configStreams.forEach((streamItem, index) => {
        const stream = {
          ...streamItem,
          ...streamItem['l2-config'],
          ...streamItem['l3-ipv4-config'],
          ...streamItem['l3-ipv6-config']
        };

        if (stream['cbs-size'] > 0) {
          streamObj[Y1564ConfigFieldMapping.CBS] = Number(stream['cbs-size']);
          streamObj[Y1564ConfigFieldMapping.CBS_REPETITIONS] = Number(stream['cbs-number-of-reps']);
          streamObj[Y1564ConfigFieldMapping.CBS_LINE_RATE] = stream['cbs-remote-line-rate'];
          streamObj[Y1564ConfigFieldMapping.KB_SIZE] = stream['cbs-kilo-size'];
        } else {
          if (agentType === AGENT_MODEL.QT600) {
            streamObj[Y1564ConfigFieldMapping.CBS] = 0;
            streamObj[Y1564ConfigFieldMapping.KB_SIZE] = 'cbs1000';
            streamObj[Y1564ConfigFieldMapping.CBS_REPETITIONS] = 1;
            streamObj[Y1564ConfigFieldMapping.CBS_LINE_RATE] = 'Mbps1000';
          }
        }
        streamObj[Y1564ConfigFieldMapping.STREAM_NUMBER] =
          Number(stream['stream-number']) || index + 1;
        streamObj[Y1564ConfigFieldMapping.STREAM_NAME] = stream['name'];
        if (stream[Y1564ConfigFieldMapping.OVER_HEAD_BYTES] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.OVER_HEAD_BYTES] =
            stream[Y1564ConfigFieldMapping.OVER_HEAD_BYTES];
        }
        if (stream['fail-on-OOS'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.ERROR_ON_OSS] =
            stream['fail-on-OOS'] === 'true' ? true : false;
        } else {
          streamObj[Y1564ConfigFieldMapping.ERROR_ON_OSS] = undefined;
        }
        streamObj[Y1564ConfigFieldMapping.CIR] = Number(stream['cir-from-engine-a']);
        if (stream['cir-from-engine-b'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.CIR2] = Number(stream['cir-from-engine-b']);
        }
        streamObj[Y1564ConfigFieldMapping.EIR] = Number(stream['eir-from-engine-a']);
        if (stream['eir-from-engine-b'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.EIR2] = Number(stream['eir-from-engine-b']);
        }
        streamObj[Y1564ConfigFieldMapping.POLICING] =
          stream['run-policing-from-engine-a'] === 'true' ? true : false;
        streamObj[Y1564ConfigFieldMapping.POLICING2] =
          stream['run-policing-from-engine-b'] === 'true' ? true : false;
        if (stream['max-ir-from-engine-a'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.MAX_IR] = Number(stream['max-ir-from-engine-a']);
        }
        if (stream['max-ir-from-engine-b'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.MAX_IR2] = Number(stream['max-ir-from-engine-b']);
        }
        if (stream['frame-loss-ratio-threshold'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.LOSS] = Number(stream['frame-loss-ratio-threshold']);
        }

        if (stream['frame-delay-threshold'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.FD] = Number(stream['frame-delay-threshold']);
        } else {
          streamObj[Y1564ConfigFieldMapping.FD] = null;
        }
        if (stream['latency-FDV-maximum'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.FDV] = Number(stream['latency-FDV-maximum']);
        }
        if (stream['flow-label'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.FLOW_LABEL] = Number(stream['flow-label']);
        }
        if (stream['hop-limit'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.HOP_LIMIT] = Number(stream['hop-limit']);
        }
        if (stream['traffic-class'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.TRAFFIC_CLASS] = stream['traffic-class'];
        }
        if (stream['tos'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.TOS] = Number(stream['tos']);
        }
        if (stream['ttl'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.TTL] = Number(stream['ttl']);
        }
        if (stream['payload'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.PAYLOAD_TYPE] = stream['payload'];
        }
        if (stream['sct-frame-size-list'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.SCT_FRAME_SIZE] = 512;
        }
        if (stream['spt-frame-size-list'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.SPT_FRAME_SIZE] = 512;
        }

        if (stream['stream-in-use'] === undefined) {
          streamObj[Y1564ConfigFieldMapping.STREAM_IN_USE] = true;
        } else {
          streamObj[Y1564ConfigFieldMapping.STREAM_IN_USE] =
            stream['stream-in-use'] === 'true' ? true : false;
        }
        if (stream['inner-vlan-priority'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.CVLAN_PRIORITY] = Number(stream['inner-vlan-priority']);
        }
        if (stream['vlan-priority'] !== undefined) {
          streamObj[Y1564ConfigFieldMapping.SVLAN_PRIORITY] = Number(stream['vlan-priority']);
        }

        streamObj = this.removeNullValues(streamObj);
        streamData = [...streamData, { ...streamObj }];
      });
    }
    return streamData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  removeNullValues(props: any): any {
    const target = {};
    if (props) {
      Object.entries(props).forEach(([k, v]) => !isNil(v) && !Number.isNaN(v) && (target[k] = v));
    }
    return target;
  }

  //eslint-disable-next-line
  getY1564ExtendedPluginConfig(params: any): any {
    const pluginConfig = this.testConfig['config']['test']?.['extended-config']?.['pluginConfig'];
    //default address type for edit mode
    let addressType = definedAddressValue.MANUAL;
    if (pluginConfig) {
      if (params[Y1564ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L2) {
        addressType =
          pluginConfig.connections?.connections?.[0]?.['source']?.['layer2SrcType'] || addressType;
      } else {
        if (params[Y1564ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4) {
          addressType =
            pluginConfig.connections?.connections?.[0]?.['source']?.['ipv4SrcType'] || addressType;
        } else {
          addressType =
            pluginConfig.connections?.connections?.[0]?.['source']?.['ipv6SrcType'] || addressType;
        }
      }
    }
    return addressType;
  }

  getFlatternedStreamData(streamData: Array<any>, agentType: string): Array<any> {
    const flatternedStreamData = [];
    streamData.forEach(stream => {
      if (!stream[Y1564ConfigFieldMapping.CBS] && agentType === AGENT_MODEL.QT600) {
        stream[Y1564ConfigFieldMapping.CBS] = 0;
        stream[Y1564ConfigFieldMapping.KB_SIZE] = 'cbs1000';
        stream[Y1564ConfigFieldMapping.CBS_REPETITIONS] = 1;
        stream[Y1564ConfigFieldMapping.CBS_LINE_RATE] = 'Mbps1000';
      }
      if (stream.strmInUse === undefined) {
        stream[Y1564ConfigFieldMapping.STREAM_IN_USE] = true;
      }
      const data = {
        ...stream,
        ...stream['l2-config'],
        ...stream['l3-ipv4-config'],
        ...stream['l3-ipv6-config']
      };
      flatternedStreamData.push(data);
    });
    return flatternedStreamData;
  }

  // Load common params from the selected device entity.
  getCommonAgentTestparams(): any {
    const config = {};
    config[TruespeedConfigFieldMapping.ALIAS] =
      this.mode === 'edit'
        ? this.selectedTestEntity.getAlias().substring(0, 80)
        : this.selectedTestEntity.getAlias().substring(0, 70) + '-CLONE';
    const customer = this.testConfig['config']['test']?.['test-list-data']?.['customer'];
    config[TruespeedConfigFieldMapping.CUSTOMER] = customer ? customer.name : '';
    config[TruespeedConfigFieldMapping.CONTACT] = customer ? customer.contact || '' : '';
    config[TruespeedConfigFieldMapping.EMAIL] = customer ? customer.email || '' : '';
    config[TruespeedConfigFieldMapping.PHONE] = customer ? customer.phone || '' : '';
    const technician =
      this.testConfig['config']['test']?.['extended-config']?.['technicianInfo']?.name;
    const technicianId =
      this.testConfig['config']['test']?.['extended-config']?.['technicianInfo']?.id;
    const comments = this.testConfig['config']['test']?.['extended-config']?.['testInfo']?.comments;
    config[TruespeedConfigFieldMapping.COMMENTS] = comments ? comments : '';
    config[TruespeedConfigFieldMapping.TECHINICIAN] = technician ? technician : '';
    config[TruespeedConfigFieldMapping.TECHINICIANID] = technicianId ? technicianId : '';
    const testPoints = this.testConfig['config']['test']?.['extended-config']?.['testPoints'];
    if (testPoints) {
      let originatorId = testPoints?.[0]?.id;
      const originatorPhyId = testPoints?.[0]?.testPortPhyId;
      if (originatorPhyId) {
        originatorId = originatorPhyId === 'p1' ? originatorId + '-port1' : originatorId + '-port2';
      }
      const orginatorDevice = this.deviceService.getDeviceById(originatorId);
      if (originatorId) {
        config[TruespeedConfigFieldMapping.SELECTED_ORIGINATOR] = orginatorDevice;
      }
      let destinationId = testPoints?.[1]?.id;
      const destinationPhyId = testPoints?.[1]?.testPortPhyId;
      if (destinationPhyId) {
        destinationId =
          destinationPhyId === 'p1' ? destinationId + '-port1' : destinationId + '-port2';
      }
      const agentModel = testPoints?.[1]?.model;
      const destinationDevice =
        agentModel === 'unmanaged'
          ? this.unmanagedDevicesService.findDeviceByID(destinationId)
          : this.deviceService.findDeviceByID(destinationId);
      config[TruespeedConfigFieldMapping.SELECTED_DESTINATION] = destinationDevice;
      config[TruespeedConfigFieldMapping.AGENT_TYPE] = orginatorDevice?.getModel();
    }
    //no extended config info
    else {
      const originatorId = this.selectedTestEntity.getOriginatorAgent()?.deviceData?.id;
      const destinationId = this.selectedTestEntity.getDestinationAgent()?.deviceData?.id;
      if (originatorId) {
        const orginatorDevice = this.deviceService.getDeviceById(originatorId);
        config[TruespeedConfigFieldMapping.SELECTED_ORIGINATOR] = orginatorDevice;
        config[TruespeedConfigFieldMapping.AGENT_TYPE] = orginatorDevice.getModel();
      }
      if (destinationId) {
        const destinationDevice = this.deviceService.getDeviceById(destinationId);
        config[TruespeedConfigFieldMapping.SELECTED_DESTINATION] = destinationDevice;
      }
    }

    return config;
  }

  //eslint-disable-next-line
  getTestParams(testId: string, testConfig: any): string {
    let optionValue = '';
    if (testConfig && testId) {
      const primaryConfig =
        testConfig?.config?.config?.[0]?.primaryConfig['ietf-lmap-control:lmap'];
      if (primaryConfig) {
        const testOptions = primaryConfig.schedules.schedule[0].action[0].option;
        const option = testOptions.find(opt => opt.id === testId);
        optionValue = option ? option.value : '';
      }
    }
    return optionValue ? JSON.parse(optionValue) : optionValue;
  }

  //eslint-disable-next-line
  getTestParamsById(testId: string, testConfig: any): string {
    let optionValue = '';
    if (testConfig && testId) {
      const primaryConfig =
        testConfig?.config?.config?.[0]?.primaryConfig['ietf-lmap-control:lmap'];
      if (primaryConfig) {
        const testOptions = primaryConfig.schedules.schedule[0].action[0].option;
        const option = testOptions.find(opt => opt.id === testId);
        optionValue = option ? option.value : '';
      }
    }
    return optionValue;
  }

  //eslint-disable-next-line
  getPeriodicInterval(testConfig: any): any {
    let optionValue = '';
    if (testConfig) {
      const primaryConfig =
        testConfig?.config?.config?.[0]?.primaryConfig['ietf-lmap-control:lmap'];
      if (primaryConfig) {
        const option = primaryConfig.events.event[0].periodic.interval;
        optionValue = option ? option : '';
      }
    }
    return optionValue;
  }
  //eslint-disable-next-line
  getScheduledParams(testConfig: any): SchedulerFormat {
    const scheduleValue: SchedulerFormat = {
      repeatInterval: 0,
      timeStamp: '',
      endTime: '',
      isValid: true
    };
    if (testConfig) {
      if (testConfig?.config && testConfig?.config?.config.length > 0) {
        const primaryConfig = testConfig.config.config[0]?.primaryConfig['ietf-lmap-control:lmap'];
        if (primaryConfig) {
          const event = primaryConfig.events.event[0];
          scheduleValue.repeatInterval =
            event?.periodic?.interval === 1 ? 0 : event?.periodic?.interval;
          scheduleValue.endTime = event?.periodic?.end;
          scheduleValue.timeStamp = event?.periodic?.start;
        }
      }
    }
    return scheduleValue;
  }

  //eslint-disable-next-line
  getTestType(testConfig: any): string {
    let testType = '';
    if (testConfig) {
      const primaryConfig = testConfig?.config?.[0]?.primaryConfig['ietf-lmap-control:lmap'];
      if (primaryConfig) {
        testType = primaryConfig.schedules.schedule[0].action[0].task;
      }
    }
    return testType;
  }
}
